import { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { getSupabase } from "@/lib/supabase";

interface Product {
  id: string;
  name: string;
  product_code: string;
  barcode: string;
  brand: string;
  category_id: string | null;
  category_name?: string;
  sale_price: number;
  purchase_price: number;
  stock: number;
  shelf_location: string;
}

type StockFilter = "all" | "low" | "out" | "ok";

const filters: { id: StockFilter; label: string; color: string }[] = [
  { id: "all", label: "Tümü",        color: "bg-foreground-700 text-background-50" },
  { id: "ok",  label: "Stokta",      color: "bg-emerald-500 text-background-50"   },
  { id: "low", label: "Düşük Stok",  color: "bg-amber-500 text-background-50"     },
  { id: "out", label: "Stok Yok",    color: "bg-accent-500 text-background-50"    },
];

export default function WarehouseStockReportPage() {
  const { user } = useAuth();
  const dealerId = user?.dealerId || user?.id || "";

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading]   = useState(true);
  const [query, setQuery]       = useState("");
  const [activeFilter, setActiveFilter] = useState<StockFilter>("all");
  const [filterCat, setFilterCat]       = useState("");
  const [categories, setCategories]     = useState<{ id: string; name: string }[]>([]);

  const load = useCallback(async () => {
    if (!dealerId) return;
    setLoading(true);
    const supabase = getSupabase();
    const [prodRes, catRes] = await Promise.all([
      supabase.from("products")
        .select("id, name, product_code, barcode, brand, category_id, sale_price, purchase_price, stock, shelf_location")
        .eq("dealer_id", dealerId)
        .order("name"),
      supabase.from("categories").select("id, name").eq("dealer_id", dealerId),
    ]);

    const cats = (catRes.data || []) as { id: string; name: string }[];
    setCategories(cats);
    const catMap: Record<string, string> = {};
    for (const c of cats) catMap[c.id] = c.name;

    setProducts(
      ((prodRes.data || []) as Product[]).map((p) => ({
        ...p,
        category_name: p.category_id ? catMap[p.category_id] || "" : "",
      }))
    );
    setLoading(false);
  }, [dealerId]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      const q = query.trim().toLowerCase();
      const matchQ = !q || [p.name, p.product_code, p.barcode, p.brand]
        .some((v) => v?.toLowerCase().includes(q));
      const matchCat = !filterCat || p.category_id === filterCat;
      const matchFilter =
        activeFilter === "all" ? true :
        activeFilter === "ok"  ? p.stock > 3 :
        activeFilter === "low" ? p.stock > 0 && p.stock <= 3 :
        p.stock <= 0;
      return matchQ && matchCat && matchFilter;
    });
  }, [products, query, filterCat, activeFilter]);

  const totalValue   = products.reduce((s, p) => s + p.stock * p.purchase_price, 0);
  const saleValue    = products.reduce((s, p) => s + p.stock * p.sale_price, 0);
  const totalStock   = products.reduce((s, p) => s + p.stock, 0);
  const lowCount     = products.filter((p) => p.stock > 0 && p.stock <= 3).length;
  const outCount     = products.filter((p) => p.stock <= 0).length;

  const exportCSV = () => {
    const header = "Ürün Adı,Kod,Barkod,Kategori,Raf,Stok,Alış Fiyatı,Satış Fiyatı,Stok Değeri (Alış)";
    const rows = filtered.map((p) =>
      [p.name, p.product_code, p.barcode, p.category_name, p.shelf_location,
       p.stock, p.purchase_price, p.sale_price, (p.stock * p.purchase_price).toFixed(2)]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `depo-stok-raporu-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950">Depo Stok Raporu</h4>
          <p className="text-sm text-foreground-500 mt-0.5">Mevcut stok durumu ve değerleri</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 text-foreground-600 rounded-lg text-sm cursor-pointer hover:bg-background-200/70">
            <i className={`ri-refresh-line ${loading ? "animate-spin" : ""}`}></i> Yenile
          </button>
          <button onClick={exportCSV} className="flex items-center gap-2 px-3 py-2 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-lg text-sm font-medium cursor-pointer">
            <i className="ri-download-line"></i> CSV İndir
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Toplam Ürün Çeşidi", val: products.length,              icon: "ri-box-3-line",         color: "text-foreground-950"   },
          { label: "Toplam Stok Adedi",  val: totalStock,                    icon: "ri-stack-line",         color: "text-foreground-950"   },
          { label: "Düşük / Stok Yok",  val: `${lowCount} / ${outCount}`,   icon: "ri-alert-line",         color: "text-amber-700"        },
          { label: "Alış Değeri",        val: `₺${totalValue.toLocaleString("tr-TR", {minimumFractionDigits:2, maximumFractionDigits:2})}`, icon: "ri-money-dollar-circle-line", color: "text-emerald-700" },
        ].map((kpi) => (
          <div key={kpi.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <i className={`${kpi.icon} text-xl ${kpi.color} block mb-1`}></i>
            <p className={`text-lg font-bold ${kpi.color}`}>{kpi.val}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{kpi.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ürün ara..."
            className="w-full pl-9 pr-4 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
          />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)}
          className="px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm focus:outline-none cursor-pointer">
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <div className="flex gap-1">
          {filters.map((f) => (
            <button
              key={f.id}
              onClick={() => setActiveFilter(f.id)}
              className={`px-3 py-2 rounded-lg text-xs font-medium cursor-pointer transition-colors ${
                activeFilter === f.id ? f.color : "bg-background-100 text-foreground-600 hover:bg-background-200/70"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {loading ? (
          <div className="py-20 text-center"><i className="ri-loader-4-line animate-spin text-2xl text-foreground-400"></i></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <i className="ri-box-3-line text-3xl text-foreground-300 block mb-2"></i>
            <p className="text-sm text-foreground-400">Ürün bulunamadı</p>
          </div>
        ) : (
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-380px)]">
            <table className="w-full text-sm min-w-[700px]">
              <thead className="bg-background-100">
                <tr>
                  {["Ürün Adı", "Kod", "Kategori", "Raf", "Stok", "Alış Fiyatı", "Satış Fiyatı", "Stok Değeri"].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-foreground-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-t border-background-200/70 hover:bg-background-100/40">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground-950">{p.name}</p>
                      {p.brand && <p className="text-xs text-foreground-400">{p.brand}</p>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-foreground-500">{p.product_code || "—"}</td>
                    <td className="px-4 py-3 text-xs text-foreground-500">{p.category_name || "—"}</td>
                    <td className="px-4 py-3 text-xs text-foreground-500">{p.shelf_location || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        p.stock <= 0 ? "bg-accent-100 text-accent-700" :
                        p.stock <= 3  ? "bg-amber-100 text-amber-700"  :
                        "bg-emerald-100 text-emerald-700"
                      }`}>
                        {p.stock} adet
                      </span>
                    </td>
                    <td className="px-4 py-3 text-foreground-600">₺{Number(p.purchase_price).toFixed(2)}</td>
                    <td className="px-4 py-3 font-semibold text-foreground-950">₺{Number(p.sale_price).toFixed(2)}</td>
                    <td className="px-4 py-3 text-foreground-700 font-medium">
                      ₺{(p.stock * p.purchase_price).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-background-100 border-t-2 border-background-200/70">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-xs font-medium text-foreground-500">{filtered.length} ürün</td>
                  <td className="px-4 py-3 font-bold text-foreground-950">{filtered.reduce((s, p) => s + p.stock, 0)} adet</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 font-bold text-foreground-950">
                    ₺{filtered.reduce((s, p) => s + p.stock * p.purchase_price, 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
